import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "../../../api";
import { MIGRATION } from "../../../constants/testIds";
import { useAppData } from "../../../contexts/AppDataContext";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { SubmitButton } from "../../common/SubmitButton";

// SCR-P04-50 - per-branch parallel/live toggle. Going live is hard-gated server-side by the
// go-live checklist (CHECKLIST_INCOMPLETE 409 if integrations aren't connected or counts don't
// reconcile) - this screen surfaces that 409's reason rather than silently failing.
export function ParallelRunTab() {
  const queryClient = useQueryClient();
  const { branches } = useAppData();
  const [goLiveTarget, setGoLiveTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const { data: states } = useQuery({ queryKey: ["parallel-run"], queryFn: () => api.get("/parallel-run").then((r) => r.data.states) });

  const setModeMutation = useMutation({
    mutationFn: ({ branchId, mode }) => api.post(`/parallel-run/${branchId}`, { mode }),
    onSuccess: () => { toast.success("Branch is now live"); setGoLiveTarget(null); setConfirmText(""); queryClient.invalidateQueries({ queryKey: ["parallel-run"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Checklist not satisfied - see Go-live checklist tab"),
  });

  const modeFor = (branchId) => states?.find((s) => s.branch_id === branchId)?.mode || "parallel";

  return (
    <div className="space-y-2.5">
      {branches?.map((b) => {
        const mode = modeFor(b.id);
        return (
          <div key={b.id} data-testid={MIGRATION.parallelBranchRow(b.id)} className="solid-surface rounded-[12px] p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>{b.name}</p>
              <Badge style={mode === "live" ? { background: "#3FA65B22", color: "#3FA65B", border: "none" } : { background: "var(--scarlet-surface-2)", color: "var(--scarlet-text-secondary)", border: "none" }}>
                {mode === "live" ? "Live" : "Parallel"}
              </Badge>
            </div>
            {mode === "live" ? (
              <Button data-testid={MIGRATION.parallelModeToggle(b.id)} size="sm" variant="outline" onClick={() => setModeMutation.mutate({ branchId: b.id, mode: "parallel" })}>Revert to parallel</Button>
            ) : (
              <Button data-testid={MIGRATION.parallelModeToggle(b.id)} size="sm" onClick={() => setGoLiveTarget(b)} style={{ background: "var(--scarlet-accent)", color: "#fff" }}>Go live</Button>
            )}
          </div>
        );
      })}

      <Dialog open={!!goLiveTarget} onOpenChange={(v) => { if (!v) { setGoLiveTarget(null); setConfirmText(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Go live for {goLiveTarget?.name}?</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>
            This is blocked unless the go-live checklist (integrations connected + counts reconciled) is fully satisfied. Type <strong>GO LIVE</strong> to confirm.
          </p>
          <div className="space-y-3">
            <Input data-testid={MIGRATION.goLiveConfirmInput} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="GO LIVE" />
            <SubmitButton
              testId={MIGRATION.goLiveConfirmSubmit} disabled={confirmText !== "GO LIVE"} pending={setModeMutation.isPending} fullWidth
              onClick={() => setModeMutation.mutate({ branchId: goLiveTarget.id, mode: "live" })} label="Confirm go-live"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
